import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatSlideToggle,
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import {
  HangMap,
  PLACE_RESULT_FIELDS,
} from '@components/hang-map/hang-map.component';
import { SelectDialog } from '@components/select-dialog/select-dialog.component';
import { environment } from '@env/environment';
import { Loader } from '@googlemaps/js-api-loader';
import { HttpStoreFacade } from '@http/store/facade';
import { DEFAULT_EMPTY_LOCATION } from '@utils/defaults';
import { Location } from 'pueblo-types';
import {
  BehaviorSubject,
  debounceTime,
  defer,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { ILocationForm, assertIsLocationForm } from '../form-types';
import { SelectLocation } from './select-location.component';

@Component({
  selector: 'location-form',
  templateUrl: 'location-form.component.html',
  styleUrl: 'location-form.component.scss',
  imports: [
    HangMap,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    SelectLocation,
    SelectDialog,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationForm implements OnInit, AfterViewInit {
  private readonly rootForm = inject(FormGroupDirective);
  private readonly destroyRef = inject(DestroyRef);
  private readonly httpStore = inject(HttpStoreFacade);

  readonly virtualToggleVisible = input(false);
  readonly compact = input(false);
  readonly showMapPreview = input(false);

  private readonly loader = new Loader({
    apiKey: environment.mapsApiKey,
    version: 'weekly',
  });

  protected readonly locationOptions = signal<
    google.maps.places.AutocompleteSuggestion[]
  >([]);

  protected readonly locationInput =
    viewChild.required<ElementRef<HTMLInputElement>>('locationInput');
  protected readonly locationDialog =
    viewChild.required<SelectDialog>('locationDialog');

  protected readonly $virtualToggle =
    viewChild<MatSlideToggle>('virtualToggle');

  protected readonly $virtual = signal<boolean>(false);
  private prevVirtual = false;

  // For controlling location input floating label.
  protected readonly $locationFieldContainer = viewChild.required<
    ElementRef<HTMLElement>
  >('locationFieldContainer');
  protected readonly $locationInputFocused = signal(false);
  protected readonly $locationNameValue = signal('');
  protected readonly $locationAddressValue = signal('');

  protected readonly locationNameInput$ = new BehaviorSubject('');

  readonly formPath = input.required<string[]>();

  protected form!: FormGroup<ILocationForm>;

  protected readonly selectedLocation = signal<Location | undefined>(undefined);

  private locationCache = new Map<string, Location>();

  constructor() {
    effect(() => {
      const location = this.selectedLocation();
      if (!location) return;

      if (this.locationCache.has(location.displayName)) return;
      if (location.coords.lat !== 0 && location.coords.lng !== 0) {
        this.locationCache.set(location.displayName, location);
      }
    });

    effect(() => {
      const virtual = this.$virtual();
      if (virtual === this.prevVirtual) return;
      if (virtual) {
        this.form.controls.locationName.setValue('Virtual');
        this.form.controls.locationName.disable();
      } else {
        this.form.controls.locationName.setValue('');
        this.form.controls.locationName.enable();
      }
      this.prevVirtual = virtual;
    });

    effect(() => {
      if (this.$locationInputFocused()) {
        this.httpStore.refreshUserLocation();
      }
    });

    // Manually control floating label for location input because the Maps
    // input always adds a placeholder. The view sets the placeholder to '&nbsp;'
    // to appear that there isn't one, while this code manually floats and hides
    // the floating label.
    effect(() => {
      const container = this.$locationFieldContainer();
      const focused = this.$locationInputFocused();
      const name = this.$locationNameValue();
      const address = this.$locationAddressValue();
      const value = !!name || !!address;

      const virtualToggle = this.$virtualToggle();
      if (
        virtualToggle &&
        name.toLocaleLowerCase() !== 'virtual' &&
        address.toLocaleLowerCase() !== 'virtual'
      ) {
        untracked(() => this.$virtual.set(false));
      }

      const floatingLabel = container.nativeElement.querySelector(
        '.mdc-floating-label',
      ) as HTMLElement;
      if (!floatingLabel) return;

      floatingLabel.classList.remove('ng-star-inserted');
      const parent = floatingLabel.parentElement;
      if (!parent) return;
      if (focused || value) {
        floatingLabel.style.minWidth = '';
        floatingLabel.classList.add('mdc-floating-label--float-above');
        parent.style.width =
          'calc(30px * var(--mat-mdc-form-field-floating-label-scale, 0.75) + 9px)';
        parent.style.borderTop = '';
      } else {
        floatingLabel.style.minWidth = '70px';
        floatingLabel.classList.remove('mdc-floating-label--float-above');
        parent.style.width = 'auto';
        if (focused) {
          parent.style.borderTop =
            '2px solid var(--mdc-outlined-text-field-outline-color)';
        } else {
          parent.style.borderTop =
            '1px solid var(--mdc-outlined-text-field-outline-color)';
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const form = this.rootForm.control.get(this.formPath());
    assertIsLocationForm(form);
    this.form = form;

    if (form.controls.latitude.value && form.controls.longitude.value) {
      this.selectedLocation.set({
        displayName: form.controls.locationName.value,
        address: form.controls.locationAddress.value,
        coords: {
          lat: form.controls.latitude.value,
          lng: form.controls.longitude.value,
        },
      });
    }

    this.form.controls.locationName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        if (this.selectedLocation()?.displayName !== value) {
          if (this.locationCache.has(value)) {
            this.updateLocation(this.locationCache.get(value)!);
            return;
          }
          this.selectedLocation.set(DEFAULT_EMPTY_LOCATION);
        }
      });

    this.form.controls.locationName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.$locationNameValue.set(value);

        if (this.selectedLocation()?.displayName !== value) {
          if (this.locationCache.has(value)) {
            this.updateLocation(this.locationCache.get(value)!);
            return;
          }
          this.selectedLocation.set(DEFAULT_EMPTY_LOCATION);
        }
      });

    this.form.controls.locationAddress.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.$locationAddressValue.set(value);
      });

    await this.initAutocomplete();
  }

  ngAfterViewInit(): void {
    this.$locationNameValue.set(this.form.controls.locationName.value);
    this.$locationAddressValue.set(this.form.controls.locationAddress.value);
  }

  protected updateLocation(location: Location): void {
    if (
      location.address &&
      location.displayName &&
      location.coords.lat &&
      location.coords.lng
    ) {
      this.selectedLocation.set(location);
      this.form.controls.locationAddress.setValue(location.address);
      this.form.controls.locationName.setValue(location.displayName);
      this.form.controls.latitude.setValue(location.coords.lat);
      this.form.controls.longitude.setValue(location.coords.lng);
    }
  }

  protected openLocationDialog(event: Event) {
    event.stopPropagation();
    this.httpStore.refreshUserLocation();
    this.locationDialog().open();
  }

  protected toggleVirtual(change: MatSlideToggleChange) {
    this.$virtual.set(change.checked);
  }

  protected updateLocationName(event: Event) {
    if (
      event instanceof InputEvent &&
      event.target instanceof HTMLInputElement
    ) {
      this.form.controls.locationName.setValue(event.target.value);
      this.locationNameInput$.next(event.target.value);
    }
  }

  protected autocompleteDisplayFn(
    suggestion: google.maps.places.AutocompleteSuggestion,
  ): string {
    return suggestion.placePrediction?.mainText?.text ?? '';
  }

  private async initAutocomplete() {
    const { AutocompleteSessionToken, AutocompleteSuggestion } =
      await this.loader.importLibrary('places');

    this.locationNameInput$
      .pipe(
        tap((value) => {
          if (!value) this.locationOptions.set([]);
        }),
        debounceTime(300),
        filter((value) => !!value),
        map((value) => ({
          input: value,
          sessionToken: new AutocompleteSessionToken(),
        })),
        switchMap((request) =>
          defer(
            async () =>
              await AutocompleteSuggestion.fetchAutocompleteSuggestions(
                request,
              ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.locationOptions.set(value.suggestions);
      });
  }

  protected async autocompleteOptionSelected(
    event: MatAutocompleteSelectedEvent,
  ) {
    const suggestion = event.option
      .value as google.maps.places.AutocompleteSuggestion;
    const place = (
      await suggestion.placePrediction
        ?.toPlace()
        .fetchFields({ fields: PLACE_RESULT_FIELDS })
    )?.place;

    const location = place?.location;
    if (!location) throw 'Failed to return location from Autocomplete';

    const selectionPosition = { lat: location.lat(), lng: location.lng() };

    this.updateLocation({
      displayName: place.displayName ?? '',
      address: place.formattedAddress ?? '',
      coords: selectionPosition,
    });
  }
}
